/* eslint-disable max-len */
import { useCallback, useEffect, useMemo, useRef } from 'react';

import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  ColumnDef,
  getSortedRowModel,
  SortingState,
  OnChangeFn,
  RowSelectionState,
} from '@tanstack/react-table';
import { compact } from 'lodash';
import { TbDotsVertical } from 'react-icons/tb';
import Checkbox from 'ui/components/form/Checkbox';
import NavAction from 'ui/components/nav/NavAction';
import { NavOption } from 'ui/components/nav/NavAction/NavAction';
import { cn } from 'utils/helpers';
import type { CheckboxProps } from 'ui/components/form/Checkbox/Checkbox';
import { HiMiniTrash } from 'react-icons/hi2';
import { BsFillPencilFill } from 'react-icons/bs';

import LoadingTable from './LoadingTable';

type SortingProps =
  | {
      clientSideSorting: true;
      serverSideSorting?: false;
    }
  | {
      clientSideSorting?: false;
      serverSideSorting?: true;
      sorting?: SortingState;
      setSorting?: OnChangeFn<SortingState>;
    };

type PROPS<TData> = SortingProps & {
  data: TData[];
  columns: ColumnDef<TData>[];
  size?: 'sm' | 'md';
  withCheckbox?: boolean;
  sorting?: SortingState;
  selected?: RowSelectionState;
  setSorting?: OnChangeFn<SortingState>;
  setSelected?: OnChangeFn<RowSelectionState>;
  actions?: (row: TData) => NavOption[];
  isLoading?: boolean;
  onSelectRow?: (id: string) => void;
  onClickDelete?: () => void;
  onClickEdit?: () => void;
  canEdit?: boolean;
  canDelete?: boolean;
  headerClasses?: string;
  bodyClasses?: string;
  ['data-tut']?: string;
};

function IndeterminateCheckbox({
  indeterminate,
  className = '',
  ...rest
}: { indeterminate?: boolean } & CheckboxProps) {
  const ref = useRef<any>(null!);

  useEffect(() => {
    if (typeof indeterminate === 'boolean') {
      ref.current.indeterminate = !rest.checked && indeterminate;
    }
  }, [ref, indeterminate, rest.checked]);

  return (
    <Checkbox
      type="checkbox"
      ref={ref as any}
      className={className + 'cursor-pointer'}
      {...rest}
    />
  );
}

const Table = <
  TData extends {
    [key: string]: any;
  },
>({
  withCheckbox = false,
  data,
  columns: _columns,
  size = 'sm',
  clientSideSorting, //TODO: For Storybook purposes. Will not be used in production
  serverSideSorting,
  sorting,
  actions,
  setSorting,
  setSelected,
  selected,
  isLoading = false,
  onSelectRow,
  onClickDelete,
  onClickEdit,
  canDelete = true,
  canEdit = true,
  headerClasses,
  bodyClasses,
  ...rest
}: PROPS<TData>) => {
  const memoizedData = useMemo(() => data, [data]);
  const memoizedColumns = useMemo(() => _columns, [_columns]);

  const columns = useMemo<ColumnDef<TData>[]>(() => {
    return compact([
      withCheckbox && {
        id: 'select',
        header: ({ table }) => (
          <IndeterminateCheckbox
            {...{
              checked: table.getIsAllRowsSelected(),
              indeterminate: table.getIsSomeRowsSelected(),
              onChange: table.getToggleAllRowsSelectedHandler(),
            }}
          />
        ),
        cell: ({ row }) => (
          <div className="flex items-center justify-center">
            <IndeterminateCheckbox
              {...{
                checked: row.getIsSelected(),
                disabled: !row.getCanSelect(),
                indeterminate: row.getIsSomeSelected(),
                onChange: row.getToggleSelectedHandler(),
              }}
            />
          </div>
        ),
      },
      ...memoizedColumns,
    ]);
  }, [memoizedColumns, withCheckbox]);

  const table = useReactTable({
    data: memoizedData,
    columns,
    state: {
      sorting,
      rowSelection: selected,
    },
    meta: {
      size,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => {
      return row?.['id'] ?? row?.['_id'] ?? '';
    },
    enableRowSelection: true, //enable row selection for all rows
    onRowSelectionChange: setSelected,
    //TODO: For Storybook purposes. Will not be used in production
    ...(clientSideSorting && { getSortedRowModel: getSortedRowModel() }),
    ...(serverSideSorting && { manualSorting: true }),
  });

  const arrowCallback = useCallback((sort: string) => {
    switch (sort) {
      case 'asc':
        return (
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M2.50004 4.86311H9.49994C9.63536 4.86311 9.75258 4.81367 9.8515 4.7147C9.95036 4.61575 10 4.49858 10 4.36316C10 4.22774 9.95039 4.11062 9.8515 4.01154L6.35154 0.511578C6.25265 0.412742 6.1355 0.363159 6 0.363159C5.8645 0.363159 5.74735 0.412742 5.64838 0.511578L2.14842 4.01154C2.04945 4.11051 2 4.22774 2 4.36316C2 4.49855 2.04945 4.61575 2.14842 4.7147C2.2475 4.81367 2.36464 4.86311 2.50004 4.86311Z"
              fill="#23CC67"
            />
            <path
              d="M9.49994 7.13693H2.50004C2.36453 7.13693 2.24739 7.18641 2.14842 7.28527C2.04945 7.38424 2 7.50138 2 7.63681C2 7.77223 2.04945 7.88945 2.14842 7.98837L5.64838 11.4883C5.74746 11.5873 5.86461 11.6368 6 11.6368C6.13539 11.6368 6.25265 11.5873 6.35154 11.4883L9.8515 7.98834C9.95036 7.88945 10 7.77223 10 7.63678C10 7.50138 9.95039 7.38424 9.8515 7.28524C9.75261 7.1863 9.63536 7.13693 9.49994 7.13693Z"
              fill="#7E8B99"
            />
          </svg>
        );

      case 'desc':
        return (
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M2.50004 4.86311H9.49994C9.63536 4.86311 9.75258 4.81367 9.8515 4.7147C9.95036 4.61575 10 4.49858 10 4.36316C10 4.22774 9.95039 4.11062 9.8515 4.01154L6.35154 0.511578C6.25265 0.412742 6.1355 0.363159 6 0.363159C5.8645 0.363159 5.74735 0.412742 5.64838 0.511578L2.14842 4.01154C2.04945 4.11051 2 4.22774 2 4.36316C2 4.49855 2.04945 4.61575 2.14842 4.7147C2.2475 4.81367 2.36464 4.86311 2.50004 4.86311Z"
              fill="#718FA5"
            />
            <path
              d="M9.49994 7.13693H2.50004C2.36453 7.13693 2.24739 7.18641 2.14842 7.28527C2.04945 7.38424 2 7.50138 2 7.63681C2 7.77223 2.04945 7.88945 2.14842 7.98837L5.64838 11.4883C5.74746 11.5873 5.86461 11.6368 6 11.6368C6.13539 11.6368 6.25265 11.5873 6.35154 11.4883L9.8515 7.98834C9.95036 7.88945 10 7.77223 10 7.63678C10 7.50138 9.95039 7.38424 9.8515 7.28524C9.75261 7.1863 9.63536 7.13693 9.49994 7.13693Z"
              fill="#23CC67"
            />
          </svg>
        );

      default:
        return (
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M2.50004 4.86311H9.49994C9.63536 4.86311 9.75258 4.81367 9.8515 4.7147C9.95036 4.61575 10 4.49858 10 4.36316C10 4.22774 9.95039 4.11062 9.8515 4.01154L6.35154 0.511578C6.25265 0.412742 6.1355 0.363159 6 0.363159C5.8645 0.363159 5.74735 0.412742 5.64838 0.511578L2.14842 4.01154C2.04945 4.11051 2 4.22774 2 4.36316C2 4.49855 2.04945 4.61575 2.14842 4.7147C2.2475 4.81367 2.36464 4.86311 2.50004 4.86311Z"
              fill="#7E8B99"
            />
            <path
              d="M9.49994 7.1369H2.50004C2.36453 7.1369 2.24739 7.18637 2.14842 7.28524C2.04945 7.38421 2 7.50135 2 7.63678C2 7.7722 2.04945 7.88942 2.14842 7.98834L5.64838 11.4882C5.74746 11.5872 5.86461 11.6368 6 11.6368C6.13539 11.6368 6.25265 11.5872 6.35154 11.4882L9.8515 7.98831C9.95036 7.88942 10 7.7722 10 7.63675C10 7.50135 9.95039 7.38421 9.8515 7.28521C9.75261 7.18627 9.63536 7.1369 9.49994 7.1369Z"
              fill="#7E8B99"
            />
          </svg>
        );
    }
  }, []);

  if (isLoading) return <LoadingTable />;

  return (
    <div className={cn('overflow-x-auto ', data.length && 'min-h-[200px]')}>
      {(!!onClickDelete || !!onClickEdit) &&
        !!table.getFilteredSelectedRowModel()?.rows?.length && (
          <div className={cn('flex items-center gap-4', 'px-[18px] py-3')}>
            {onClickDelete && (
              <button
                disabled={!canDelete}
                type="button"
                className={cn(
                  'text-xs text-content flex items-center gap-[5px] font-semibold',
                  !canDelete && 'text-disabled',
                )}
                onClick={onClickDelete}
              >
                <HiMiniTrash className="text-base" />
                DELETE
              </button>
            )}
            {onClickEdit && (
              <button
                disabled={!canEdit}
                type="button"
                className={cn(
                  'text-xs text-content flex items-center gap-[5px] font-semibold',
                  !canEdit && 'text-disabled',
                )}
                onClick={onClickEdit}
              >
                <BsFillPencilFill className="text-base" />
                EDIT
              </button>
            )}
          </div>
        )}
      <table data-tut={rest['data-tut']} className="table-auto w-full">
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr
              key={headerGroup.id}
              className="divide-x divide-separator-primary"
            >
              {headerGroup.headers.map((header, index) => (
                <th
                  className={cn(
                    'bg-fill-primary font-normal uppercase',
                    index === 0 && withCheckbox && 'w-10 px-4',
                    size === 'sm' ? 'py-3' : 'py-4',
                    index > 0 && withCheckbox && 'px-4',
                    headerClasses,
                  )}
                  key={header.id}
                >
                  {header.isPlaceholder ? null : (
                    <div
                      onClick={header.column.getToggleSortingHandler()}
                      className={cn(
                        !withCheckbox && 'px-4',
                        'flex items-center',
                        'text-xs',
                        header.column.getCanSort() &&
                          'cursor-pointer select-none',
                      )}
                    >
                      <div
                        className={cn(
                          'flex-1',
                          'text-left',
                          'flex items-center',
                          index === 0 && withCheckbox && 'justify-center',
                        )}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                      </div>
                      {header.column.getCanSort() &&
                        arrowCallback(
                          (header.column.getIsSorted() as string) ?? null,
                        )}
                    </div>
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody className={bodyClasses}>
          {table.getRowModel().rows.map((row) => {
            return (
              <tr
                className={cn(
                  'border-b border-b-separator-primary hover:bg-fill-primary',
                  'cursor-pointer',
                )}
                key={row.id || row.original._id}
                onClick={() => onSelectRow?.(row.id || row.original._id)}
              >
                {row.getVisibleCells().map((cell, index, array) => {
                  return (
                    <td className={cn('relative')} key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                      {index === array.length - 1 &&
                        !!actions?.(row.original).length && (
                          <NavAction
                            placement="left-end"
                            className="absolute inset-y-0 right-0 inline-flex"
                            label={<TbDotsVertical className="w-5 h-5" />}
                            options={actions ? actions(row.original) : []}
                          />
                        )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          {table.getFooterGroups().map((footerGroup) => (
            <tr key={footerGroup.id}>
              {footerGroup.headers.map((header) => (
                <th key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.footer,
                        header.getContext(),
                      )}
                </th>
              ))}
            </tr>
          ))}
        </tfoot>
      </table>
    </div>
  );
};

export default Table;
